import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';

type CoachmarkProps = {
  show: boolean;
  targetRef: React.MutableRefObject<HTMLElement | null>;
  popupPlacement: 'top' | 'right' | 'bottom' | 'left';
  renderContent: () => React.ReactNode;
};

/**
 * 特定の要素にフォーカスを順番にあてて、オンボーディングを行えるコンポーネント。
 * aicalendarのみで利用されるため、現状uiではなく、appoint配下に配置しています。
 */
export const Coachmark = React.memo(({ show, targetRef, popupPlacement, renderContent }: CoachmarkProps) => {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setTargetElement(targetRef.current);
  }, [targetRef, setTargetElement]);

  if (!targetElement) {
    return null;
  }

  if (!show) {
    return null;
  }

  const bodyElement = window.document.querySelector('body');
  if (!bodyElement) {
    return null;
  }

  return ReactDOM.createPortal(
    <>
      <CoarchmarkArea targetElement={targetElement} />
      <CoachmarkPopup
        targetElement={targetElement}
        popupPlacement={popupPlacement}
        renderContent={renderContent}
      />
    </>,
    bodyElement
  );
});

const getElementRectAndSize = (element: HTMLElement) => {
  const { top, right, bottom, left } = element.getBoundingClientRect();
  const height = bottom - top;
  const width = right - left;
  return { top, right, bottom, left, height, width };
};

const areaMargin = 8;
const CoarchmarkArea = React.memo(({ targetElement }: { targetElement: HTMLElement }) => {
  const { top, left, width, height } = getElementRectAndSize(targetElement);

  const style = useMemo(
    () => ({
      width: width + areaMargin,
      height: height + areaMargin,
      top: top - areaMargin / 2,
      left: left - areaMargin / 2
    }),
    [top, left, width, height]
  );

  return <CoachmarkAreaContainer style={style} />;
});

const popupMargin = 12;
const CoachmarkAreaContainer = styled.div`
  position: fixed;
  box-shadow: 0 0 0 100vw #000000bf;
  transition: all 0.3s;
  z-index: 9999998;
  border-radius: 3px;
`;

const CoachmarkPopup = React.memo(
  ({
    targetElement,
    popupPlacement,
    renderContent
  }: Pick<CoachmarkProps, 'popupPlacement' | 'renderContent'> & { targetElement: HTMLElement }) => {
    const { top, right, bottom, left, width, height } = getElementRectAndSize(targetElement);

    const style = useMemo(() => {
      switch (popupPlacement) {
        case 'top':
          return {
            bottom: window.innerHeight - top + popupMargin,
            left: (-1 * areaMargin) / 2 + left
          };
        case 'right':
          return {
            top: top - areaMargin / 2,
            left: right + areaMargin / 2 + popupMargin
          };
        case 'bottom':
          return {
            top: bottom + popupMargin,
            left: (-1 * areaMargin) / 2 + left
          };
        case 'left':
          return {
            top: top - areaMargin / 2,
            right: window.innerWidth - left + popupMargin
          };
        default:
          throw new Error(`unknown popupPlacement:${popupPlacement}`);
      }
    }, [top, right, bottom, left, width, height, popupPlacement]);

    return (
      <CoachmarkPopupContainer placement={popupPlacement} style={style}>
        {renderContent()}
      </CoachmarkPopupContainer>
    );
  }
);

const CoachmarkPopupContainer = styled.div<{ placement: CoachmarkProps['popupPlacement'] }>`
  --tip-move: 10px;
  --tip-height: 8px;
  --tip-shift: calc(-2 * var(--tip-height));

  position: fixed;
  transition: all 0.3s;
  z-index: 9999999;
  background-color: #fff;
  padding: 8px;
  border-radius: 3px;

  &::before {
    content: '';
    border-top-width: var(--tip-height);
    border-left-width: var(--tip-height);
    border-right-width: var(--tip-height);
    border-bottom-width: var(--tip-height);
    border-style: solid;
    background: transparent;
    border-color: #ffffff;
    position: absolute;

    ${({ placement }) => {
      switch (placement) {
        case 'top':
          return css`
            bottom: var(--tip-shift);
            left: var(--tip-move);
            border-bottom-color: transparent;
            border-right-color: transparent;
            border-left-color: transparent;
          `;
        case 'right':
          return css`
            left: var(--tip-shift);
            top: var(--tip-move);
            border-top-color: transparent;
            border-left-color: transparent;
            border-bottom-color: transparent;
          `;
        case 'bottom':
          return css`
            top: var(--tip-shift);
            left: var(--tip-move);
            border-top-color: transparent;
            border-right-color: transparent;
            border-left-color: transparent;
          `;
        case 'left':
          return css`
            top: var(--tip-move);
            right: var(--tip-shift);
            border-top-color: transparent;
            border-bottom-color: transparent;
            border-right-color: transparent;
          `;
        default:
          return ``;
      }
    }}
  }
`;
